import {
  AllocationChart,
  AllocationChartItemData,
} from "@client/components/round/allocator/AllocationChart/AllocationChart";
import { OrgPage } from "@client/components/round/allocator/EvaluablePage/OrgPage";
import { AllocationChartTitle } from "@client/components/round/allocator/EvaluatorSidebar/AllocationChartHeader";
import { SidebarRoot } from "@client/components/round/allocator/EvaluatorSidebar/EvaluatorSidebar";
import { Tooltip } from "@client/components/utils/Tooltip";
import { FunderAccountsPage } from "@client/pages/round/analysis/FunderAccountsPage";
import { FunderGuaranteesPage } from "@client/pages/round/analysis/FunderGuaranteesPage";
import { RecommenderAccountsPage } from "@client/pages/round/analysis/RecommenderAccountsPage";
import { RecommenderGuaranteesPage } from "@client/pages/round/analysis/RecommenderGuaranteesPage";
import { SankeyPage } from "@client/pages/round/analysis/SankeyPage";
import { calculateSProcessResults } from "@client/utils/calculateSProcessResults";
import { sigmaAllocation } from "@client/utils/tooltips";
import userIsAdmin from "@client/utils/userIsAdmin";
import {
  hasTrackWithMatchingStatus,
  isUserFunder,
} from "@engine/round/accessors";
import { SProcessOutput } from "@lib/algoInterface/runSProcessOnRound";
import { ReactNode, useContext } from "react";
import { Redirect, Route, Switch } from "wouter";
import { StateContext } from "../Round";

/**
 * Common props needed by all analysis pages
 */
type AnalysisPageProps = {
  results: SProcessOutput;
  roundId: string;
  readyStatus: { isReady: boolean; reasonsWhyNot: string[] };
};

/**
 * A layout component for analysis pages that provides:
 * 1. Common sidebar with allocation chart
 * 2. Error handling and ready status checks
 * 3. Conditional rendering of children based on results
 */
function AnalysisLayout({
  children,
  results,
  allocations,
  readyStatus,
}: {
  children: ReactNode;
  results: SProcessOutput | Error | null;
  allocations: AllocationChartItemData[] | null;
  readyStatus: { isReady: boolean; reasonsWhyNot: string[] };
}) {
  return (
    <>
      <SidebarRoot>
        <AllocationChartTitle>
          <Tooltip title={sigmaAllocation} placement="bottom-start">
            <div>Projected final allocation</div>
          </Tooltip>
        </AllocationChartTitle>
        {!readyStatus.isReady && (
          <div>
            {`Can't run round: ${readyStatus.reasonsWhyNot.join("; ")}`}
          </div>
        )}
        {results instanceof Error && <div>{results.message}</div>}
        {allocations && <AllocationChart allocations={allocations} />}
      </SidebarRoot>
      {results && !(results instanceof Error) && children}
    </>
  );
}

/**
 * The Aggregator component serves as the router for allocation views
 * It provides routing to various analysis pages while maintaining common layout and data
 */
export function Aggregator() {
  const context = useContext(StateContext);
  const { round, actingAsUserId: userId, mode } = context;
  const { allocations, results, readyStatus } = calculateSProcessResults(round);

  // Common props shared across all analysis pages
  const pageProps: AnalysisPageProps = {
    results: results as SProcessOutput,
    roundId: round.id,
    readyStatus,
  };

  const isAdmin = userIsAdmin(userId, context);

  const accessiblePhases = [
    ...(isUserFunder(round, userId) ? ["2.initial-evaluations"] : []),
    "3.full-discussion",
    "4.evals-freeze",
    "5.complete",
  ];

  if (
    !isAdmin &&
    !hasTrackWithMatchingStatus(round, userId, mode, accessiblePhases)
  ) {
    return <Redirect to="/" />;
  }

  return (
    <Switch>
      {/* Default redirect to sankey page */}
      <Route path="/">
        <Redirect to="/sankey" />
      </Route>

      {/* Analysis view routes */}
      <Route path="/sankey">
        <AnalysisLayout
          results={results}
          allocations={allocations}
          readyStatus={readyStatus}
        >
          <SankeyPage {...pageProps} />
        </AnalysisLayout>
      </Route>
      <Route path="/funders-accountables">
        <AnalysisLayout
          results={results}
          allocations={allocations}
          readyStatus={readyStatus}
        >
          <FunderAccountsPage {...pageProps} />
        </AnalysisLayout>
      </Route>
      <Route path="/funders-guarantees">
        <AnalysisLayout
          results={results}
          allocations={allocations}
          readyStatus={readyStatus}
        >
          <FunderGuaranteesPage {...pageProps} />
        </AnalysisLayout>
      </Route>
      <Route path="/recommenders-accountables">
        <AnalysisLayout
          results={results}
          allocations={allocations}
          readyStatus={readyStatus}
        >
          <RecommenderAccountsPage {...pageProps} />
        </AnalysisLayout>
      </Route>
      <Route path="/recommenders-guarantees">
        <AnalysisLayout
          results={results}
          allocations={allocations}
          readyStatus={readyStatus}
        >
          <RecommenderGuaranteesPage {...pageProps} />
        </AnalysisLayout>
      </Route>

      {/* Evaluable detail view */}
      <Route path="/evaluable/:evaluableId" nest>
        {({ evaluableId }) => (
          <AnalysisLayout
            results={results}
            allocations={allocations}
            readyStatus={readyStatus}
          >
            <OrgPage evaluableId={evaluableId} />
          </AnalysisLayout>
        )}
      </Route>

      {/* Fallback for unknown routes */}
      <Route path="*">
        <div>Not found</div>
      </Route>
    </Switch>
  );
}

/**
 * A simple container for the main content area
 */
export const MainRoot = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="overflow-hidden flex flex-col w-full h-full">
      {children}
    </div>
  );
};
